import { AlertDialog, Button, Callout, Card, Tag } from '@humanforce/mango-ui-react';
import { useState } from 'react';
import type { PayRunApprovalDecision, PayRunApprovalDto, PayRunApprovalRequirementDto } from './payRunsApiTypes';
import { formatSystemValue } from './payRunsModel';

interface PayRunApprovalPanelProps {
  approval: PayRunApprovalDto;
  busy: boolean;
  error: string | null;
  onDecision: (requirementId: string, decision: PayRunApprovalDecision) => Promise<void>;
}

const streamLabel = (stream: string): string =>
  stream === 'eft_disbursement' ? 'EFT disbursement' : formatSystemValue(stream);

const statusTone = (requirement: PayRunApprovalRequirementDto): 'neutral' | 'positive' | 'critical' => {
  if (requirement.status === 'approved') return 'positive';
  if (requirement.status === 'rejected') return 'critical';
  return 'neutral';
};

export const PayRunApprovalPanel = ({ approval, busy, error, onDecision }: PayRunApprovalPanelProps) => {
  const [rejection, setRejection] = useState<PayRunApprovalRequirementDto | null>(null);

  const decide = async (requirementId: string, decision: PayRunApprovalDecision) => {
    await onDecision(requirementId, decision);
    if (decision === 'rejected') setRejection(null);
  };

  return (
    <section aria-labelledby="pay-run-approval-heading" className="mgo-stack mgo-gap-l">
      <div className="mgo-split">
        <div className="mgo-stack mgo-gap-xs">
          <h2 id="pay-run-approval-heading" className="mgo-text-l mgo-weight-strong">Payment approvals</h2>
          <p className="mgo-text-s mgo-color-muted">
            Decisions authorize calculation {approval.calculationHash} under route {approval.configurationIdentity} v{approval.configurationVersion}.
          </p>
        </div>
      </div>

      {error && <Callout tone="critical" title="Approval action failed">{error}</Callout>}
      {approval.isSuperseded && (
        <Callout tone="caution" title="Approval history superseded">
          These decisions are retained for audit but cannot authorize the current Pay Run.
        </Callout>
      )}
      {approval.isCalculationStale && !approval.isSuperseded && (
        <Callout tone="caution" title="Approval unavailable — the calculation has changed">
          This approval context was frozen against calculation {approval.calculationHash}, which is no longer the Pay Run's
          current calculation. Approvals can only authorize the calculation they were frozen against, so no approval,
          rejection or submission can be accepted against this context. Use Unlock to Compliance to re-run readiness and
          submit a fresh approval context. Existing approvals will not carry forward and every sign-off must be given again.
        </Callout>
      )}
      {!approval.submittedAt && !approval.isSuperseded && !approval.isCalculationStale && (
        <Callout tone="info" title="Ready to submit">
          The approval route is frozen. Submit the Pay Run to dispatch both approval streams.
        </Callout>
      )}

      <div className="mgo-grid mgo-grid-cols-tablet-2 mgo-gap-l">
        {approval.streams.map((stream) => (
          <Card key={stream.approvalStream} className="mgo-stack mgo-gap-m">
            <div className="mgo-stack mgo-gap-xs">
              <span className="mgo-text-m mgo-weight-strong">{streamLabel(stream.approvalStream)}</span>
              <span className="mgo-text-xs mgo-color-muted">{stream.requiredApprovalCount} sign-offs required</span>
            </div>
            <div className="mgo-stack mgo-gap-m">
              {stream.requirements.map((requirement) => (
                <div key={requirement.requirementId} className="mgo-stack mgo-gap-s mgo-padding-m mgo-border-neutral-soft">
                  <div className="mgo-split">
                    <div className="mgo-stack mgo-gap-xs">
                      <span className="mgo-text-s mgo-weight-strong">{formatSystemValue(requirement.approverRole)}</span>
                      <span className="mgo-text-xs mgo-color-muted">Approver: {requirement.approverUserId}</span>
                    </div>
                    <Tag label={requirement.isSuperseded ? 'Superseded' : formatSystemValue(requirement.status)} tone={requirement.isSuperseded ? 'neutral' : statusTone(requirement)} />
                  </div>
                  {requirement.decidedAt && (
                    <span className="mgo-text-xs mgo-color-muted">
                      {requirement.decidedBy} · {new Date(requirement.decidedAt).toLocaleString('en-AU')}
                    </span>
                  )}
                  {requirement.comment && <p className="mgo-text-xs">Comment: {requirement.comment}</p>}
                  {(requirement.canApprove || requirement.canReject) && (
                    <div className="mgo-cluster mgo-gap-s">
                      {requirement.canApprove && (
                        <Button label="Approve" size="small" loading={busy} onClick={() => void decide(requirement.requirementId, 'approved')} />
                      )}
                      {requirement.canReject && (
                        <Button label="Reject" size="small" variant="destructive" disabled={busy} onClick={() => setRejection(requirement)} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <AlertDialog
        visible={rejection !== null}
        header="Reject this Pay Run?"
        confirmLabel="Reject Pay Run"
        confirmLoading={busy}
        onConfirm={() => { if (rejection) void decide(rejection.requirementId, 'rejected'); }}
        onCancel={() => setRejection(null)}
      >
        Rejection cancels the Pay Run immediately and supersedes all current approval authority. The decision remains in the audit history.
      </AlertDialog>
    </section>
  );
};
